import { BusinessDataApiQueryParams } from '../interfaces/queryParams';

/**
 * Shared definition of the "Status" search-bar filter used by the admin
 * business-data lists (Courses, Providers, ...). Every one of these models has
 * the same `active` field, so the property key, label, the user-facing option
 * values and the token -> `active` mapping are defined once here rather than
 * duplicated per list.
 *
 * The two lists use different Cloudscape versions (Courses = v3 PropertyFilter,
 * Providers = v2 TablePropertyFiltering) whose token objects have different
 * shapes, so each call site normalizes its tokens into {@link StatusFilterToken}
 * before calling {@link tokensToStatusFilters}.
 *
 * Status mapping:
 *   Status = Active   -> active: true  (active records only)
 *   Status = Inactive -> active: false (inactive records only)
 *   no Status token    -> active: 'all' (backend returns both)
 */
export const STATUS_PROPERTY_KEY = 'active';
export const STATUS_PROPERTY_LABEL = 'Status';
export const STATUS_GROUP_VALUES_LABEL = 'Status values';
export const STATUS_VALUE_ACTIVE = 'Active';
export const STATUS_VALUE_INACTIVE = 'Inactive';

/** The two selectable Status option values, in display order. */
export const STATUS_FILTER_VALUES: ReadonlyArray<string> = [
    STATUS_VALUE_ACTIVE,
    STATUS_VALUE_INACTIVE,
];

/**
 * Minimal, Cloudscape-version-agnostic view of a search-bar filter token that
 * {@link tokensToStatusFilters} needs. Both the v2 and v3 token objects can be
 * narrowed to this shape.
 */
export interface StatusFilterToken {
    // The property the token filters on; absent/null for free-text tokens.
    propertyKey?: string | null;
    // Whether this is a free-text token (v2 sets this explicitly).
    isFreeText?: boolean;
    // The token value (an option value for property tokens, raw text for
    // free-text tokens).
    value: string;
}

export interface StatusFilterResult {
    searchText: string | null;
    active: BusinessDataApiQueryParams.Active;
}

/**
 * Translate a list's search-bar tokens into its filter state.
 * - A Status token maps to `active` (Active -> true, otherwise false).
 * - Any non-Status (free-text) token contributes to the name search text.
 * - No Status token means no status filtering, i.e. `active: 'all'`.
 */
export const tokensToStatusFilters = (
    tokens: ReadonlyArray<StatusFilterToken>,
): StatusFilterResult => {
    let searchText: string | null = null;
    let active: BusinessDataApiQueryParams.Active = 'all';

    tokens.forEach((token) => {
        if (token.propertyKey === STATUS_PROPERTY_KEY) {
            active = token.value === STATUS_VALUE_ACTIVE;
        } else if (token.isFreeText || !token.propertyKey) {
            searchText = searchText
                ? `${searchText} ${token.value}`
                : `${token.value}`;
        }
    });

    return { searchText, active };
};
